import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    // Store the original URL (the table page we're on)
    this.originalUrl = this.getOriginalUrl(window.location.href)
    this.loadTaskFromUrl()
    this.setupOffcanvasHandlers()
    this.interceptFormSubmissions()
    this.preventEscapeKeyUrlRestore()
  }

  getOriginalUrl(url) {
    const currentUrl = new URL(url)
    // If we're on /tasks/:id, extract just the path without the ID
    // Otherwise, keep the current path (e.g., /tasks/all)
    if (currentUrl.pathname.match(/^\/tasks\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      // We're on a direct task URL, so we'll use the redirect behavior
      // But for restoring, we'll use the referrer or default to /tasks/all
      return document.referrer || currentUrl.origin + '/tasks/all'
    }
    // Remove task_id parameter if present
    currentUrl.searchParams.delete('task_id')
    return currentUrl.toString()
  }

  setupOffcanvasHandlers() {
    // Listen for when the offcanvas frame loads content
    const offcanvasFrame = document.getElementById('updated-offcanvas-content')
    if (offcanvasFrame) {
      offcanvasFrame.addEventListener('turbo:frame-load', (event) => {
        this.showOffcanvas()
        this.updateUrlWithTaskId(event)
      })
    }
  }

  updateUrlWithTaskId(event) {
    // Extract task ID from the loaded frame's src or content
    const frame = event.target
    if (frame && frame.src) {
      console.log('Frame src:', frame.src) // Debug log

      // Check for main task loads - only match UUID patterns (8-4-4-4-12 format)
      // Exclude subtask list URLs by ensuring the UUID is not followed by /subtasks
      const taskIdMatch = frame.src.match(/\/tasks\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\?|$)/i)
      if (taskIdMatch && !frame.src.includes('/subtasks')) {
        const taskId = taskIdMatch[1]
        console.log('Updating URL to /tasks/' + taskId) // Debug log
        this.updateUrlToTaskPath(taskId)
      } else {
        console.log('Skipping URL update for:', frame.src) // Debug log
      }
    }
  }

  updateUrlToTaskPath(taskId) {
    // Change URL to /tasks/:id (not a query parameter)
    const currentUrl = new URL(window.location.href)
    const newPath = currentUrl.origin + '/tasks/' + taskId

    // Preserve tab parameter if present
    const tabParam = currentUrl.searchParams.get('tab')
    if (tabParam) {
      window.history.replaceState({}, '', newPath + '?tab=' + tabParam)
    } else {
      window.history.replaceState({}, '', newPath)
    }
  }

  restoreOriginalUrl() {
    // Restore the clean original URL (task_id already removed when we stored it)
    window.history.replaceState({}, '', this.originalUrl)
  }

  setUrlParameter(key, value) {
    const url = new URL(window.location)
    if (value) {
      url.searchParams.set(key, value)
    } else {
      url.searchParams.delete(key)
    }
    window.history.replaceState({}, '', url.toString())
  }

  showOffcanvas() {
    const offcanvas = document.getElementById('updated-offcanvas')
    if (offcanvas) {
      const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvas) || new bootstrap.Offcanvas(offcanvas)
      bsOffcanvas.show()

      // Clear task_id from URL when offcanvas is hidden and restore original URL
      // BUT only if escape key wasn't pressed
      offcanvas.addEventListener('hidden.bs.offcanvas', () => {
        if (!this.escapePressed) {
          this.restoreOriginalUrl()
        }
      }, { once: true })
    }
  }

  interceptFormSubmissions() {
    // Listen for all form submissions within the task details
    document.addEventListener('turbo:submit-start', (event) => {
      this.preserveTaskIdInFormSubmission(event)
    })
  }

  preserveTaskIdInFormSubmission(event) {
    // No longer needed since we use path-based URLs (/tasks/:id)
    // Forms will naturally work within the offcanvas context
    return
  }

  preventEscapeKeyUrlRestore() {
    // Listen for Escape key to prevent URL restoration
    this.escapeKeyHandler = (event) => {
      if (event.key === 'Escape') {
        // Mark that escape was pressed so we don't restore URL on hidden event
        this.escapePressed = true
        // Reset the flag after a brief delay
        setTimeout(() => {
          this.escapePressed = false
        }, 500)
      }
    }
    document.addEventListener('keydown', this.escapeKeyHandler, true)
  }

  disconnect() {
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler, true)
    }
  }

  loadTaskFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    const taskId = urlParams.get('task_id')
    const tabParam = urlParams.get('tab')
    const clientParam = urlParams.get('client')

    if (taskId) {
      // If client param is present, load in modal (client view)
      if (clientParam) {
        const modalFrame = document.getElementById('updated-modal-content')
        if (modalFrame) {
          const taskUrl = `/tasks/client-tasks/${taskId}`
          modalFrame.src = taskUrl
          // Show the modal
          setTimeout(() => this.showModal(), 100)
        }
      } else {
        // Load the task in the offcanvas (employee view)
        const offcanvasFrame = document.getElementById('updated-offcanvas-content')
        if (offcanvasFrame) {
          let taskUrl = `/tasks/${taskId}`
          if (tabParam) {
            taskUrl += `?tab=${tabParam}`
          }
          offcanvasFrame.src = taskUrl
          // Show the offcanvas after a brief delay to let content load
          setTimeout(() => this.showOffcanvas(), 100)
        }
      }
    }
  }

  showModal() {
    const modal = document.getElementById('updated-modal')
    if (modal) {
      const bsModal = bootstrap.Modal.getInstance(modal) || new bootstrap.Modal(modal)
      bsModal.show()
    }
  }
}
